import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faImage,
  faPlus,
  faTrash,
  faSpinner,
  faDownload,
  faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import { inspectionService } from '@/services/inspection.service';
import { LightboxViewer } from '@/components/shared/LightboxViewer';
import type { LightboxImage } from '@/components/shared/LightboxViewer';
import { toast, swalConfirm } from '@/lib/swal';

interface Props {
  inspectionId: string;
  findingId: string;
  canUpload: boolean;
  canDelete: boolean;
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 413) return 'File is too large — maximum photo size is 50 MB';
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

export function FindingPhotoStrip({ inspectionId, findingId, canUpload, canDelete }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['finding-photos', inspectionId, findingId],
    queryFn: () =>
      inspectionService.getAttachments(inspectionId, {
        attachmentType: 'PHOTO',
        findingId,
        pageSize: 10,
      }),
  });

  const photos = data?.data ?? [];
  const atLimit = photos.length >= 6;

  // Inspection photos feed Inspection Report readiness (hasPhoto) — refresh the
  // readiness gate whenever the photo set changes.
  const invalidateReadiness = () =>
    void qc.invalidateQueries({ queryKey: ['report-readiness', inspectionId] });

  const uploadMutation = useMutation({
    mutationFn: (fd: FormData) => inspectionService.uploadAttachment(inspectionId, fd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-photos', inspectionId, findingId] });
      invalidateReadiness();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to upload photo')),
  });

  const replaceMutation = useMutation({
    mutationFn: ({ attId, fd }: { attId: string; fd: FormData }) =>
      inspectionService.replaceAttachment(inspectionId, attId, fd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-photos', inspectionId, findingId] });
      invalidateReadiness();
      toast.success('Photo replaced');
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to replace photo')),
    onSettled: () => setReplacingId(null),
  });

  const deleteMutation = useMutation({
    mutationFn: (attId: string) => inspectionService.deleteAttachment(inspectionId, attId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finding-photos', inspectionId, findingId] });
      invalidateReadiness();
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to delete photo')),
  });

  const handleFiles = (files: FileList) => {
    Array.from(files).forEach((file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('attachmentType', 'PHOTO');
      fd.append('findingId', findingId);
      uploadMutation.mutate(fd);
    });
  };

  const handleReplace = (photoId: string, file: File) => {
    setReplacingId(photoId);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('attachmentType', 'PHOTO');
    replaceMutation.mutate({ attId: photoId, fd });
  };

  const lightboxImages: LightboxImage[] = photos
    .filter((p) => p.url)
    .map((p) => ({ id: p.id, url: p.url as string, filename: p.originalFileName }));

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="mb-2 flex items-center gap-2">
        <FontAwesomeIcon icon={faImage} className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          Photos ({photos.length}/6)
        </span>
        {canUpload && !atLimit && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="ml-auto flex items-center gap-1 rounded border border-dashed border-primary/40 bg-primary/5 px-2 py-0.5 text-xs text-primary transition-colors hover:bg-primary/10"
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="h-2.5 w-2.5 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faPlus} className="h-2.5 w-2.5" />
            )}
            Add
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        className="sr-only"
        onChange={(e) => {
          if (e.target.files?.length) {
            handleFiles(e.target.files);
            e.target.value = '';
          }
        }}
      />

      <input
        ref={replaceInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && replacingId) {
            handleReplace(replacingId, file);
          }
          e.target.value = '';
        }}
      />

      {isLoading ? (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <FontAwesomeIcon icon={faSpinner} className="h-2.5 w-2.5 animate-spin" />
          Loading…
        </div>
      ) : photos.length === 0 ? (
        canUpload ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
            Add first photo
          </button>
        ) : (
          <p className="text-xs text-muted-foreground">No photos.</p>
        )
      ) : (
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
          {photos.map((photo) => {
            const lbIdx = lightboxImages.findIndex((li) => li.id === photo.id);
            const isReplacing = replacingId === photo.id && replaceMutation.isPending;
            return (
              <div key={photo.id} className="group relative aspect-square">
                <button
                  className="h-full w-full overflow-hidden rounded-md bg-muted"
                  onClick={() => {
                    if (lbIdx >= 0) {
                      setLightboxIndex(lbIdx);
                      setLightboxOpen(true);
                    }
                  }}
                  aria-label={`View ${photo.originalFileName}`}
                >
                  {isReplacing ? (
                    <div className="flex h-full w-full items-center justify-center bg-muted">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="h-4 w-4 animate-spin text-muted-foreground"
                      />
                    </div>
                  ) : photo.thumbnailUrl ? (
                    <img
                      src={photo.thumbnailUrl}
                      alt={photo.originalFileName}
                      className="h-full w-full object-cover"
                    />
                  ) : photo.url ? (
                    <img
                      src={photo.url}
                      alt={photo.originalFileName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <FontAwesomeIcon
                        icon={faImage}
                        className="h-4 w-4 text-muted-foreground/40"
                      />
                    </div>
                  )}
                </button>

                {/* Delete button — top-right on hover */}
                {canDelete && (
                  <button
                    className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-red-600 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      void swalConfirm({
                        title: 'Delete Photo?',
                        text: 'This cannot be undone.',
                        confirmText: 'Delete',
                      }).then((ok) => {
                        if (ok) deleteMutation.mutate(photo.id);
                      });
                    }}
                    disabled={deleteMutation.isPending}
                    aria-label="Delete photo"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-2.5 w-2.5" />
                  </button>
                )}

                {/* Download + Replace buttons — bottom-left on hover */}
                <div className="absolute bottom-0.5 left-0.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
                  {photo.url && (
                    <a
                      href={photo.url}
                      download={photo.originalFileName}
                      onClick={(e) => e.stopPropagation()}
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-blue-600"
                      aria-label="Download photo"
                    >
                      <FontAwesomeIcon icon={faDownload} className="h-2.5 w-2.5" />
                    </a>
                  )}
                  {canUpload && (
                    <button
                      className="flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-amber-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReplacingId(photo.id);
                        replaceInputRef.current?.click();
                      }}
                      disabled={isReplacing}
                      aria-label="Replace photo"
                    >
                      <FontAwesomeIcon icon={faArrowsRotate} className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {canUpload && !atLimit && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex aspect-square items-center justify-center rounded-md border-2 border-dashed border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
              aria-label="Add photo"
            >
              <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <LightboxViewer
        images={lightboxImages}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
